/**
 * Ox Approved Installer — 8-stage dashboard.
 *
 * Replaces the legacy /my-certification page. One page per brand: shows
 * a progress bar, then collapsible cards for each of the 8 stages with
 * inline forms / uploaders / status badges.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Home, ShieldCheck, ChevronDown, ChevronUp, CheckCircle2, Clock, AlertTriangle, Search, Upload, FileText, Send, Star, Copy, Calendar as CalendarIcon, Phone, X, MapPin, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';
import LeadCoverageCard from '@/components/approval/LeadCoverageCard';
import { PortfolioSection } from '@/components/portfolio/PortfolioSection';
import { useAuth } from '@/contexts/AuthContext';

// Brand metadata.
// `accent` keeps the existing header gradient/border styling.
// `buttonBg` / `buttonText` are the brand's *primary* colour, used for every
// CTA button on this page so readability matches the rest of the brand hub.
const BRAND_META = {
  probound: { label: 'ProBound', accent: '#D4A24B', buttonBg: '#2F5E96', buttonText: '#FFFFFF', headerBg: 'linear-gradient(135deg,#0B0B0E 0%,#1f4030 75%)', back: '/probound' },
  proepoxy: { label: 'ProEpoxy', accent: '#a78bfa', buttonBg: '#7A0B7A', buttonText: '#FFFFFF', headerBg: 'linear-gradient(135deg,#0B0B0E 0%,#5b21b6 80%)', back: '/proepoxy' },
  prodecor: { label: 'ProDecor', accent: '#fb923c', buttonBg: '#D5AB09', buttonText: '#0B0B0E', headerBg: 'linear-gradient(135deg,#0B0B0E 0%,#9a3412 80%)', back: '/prodecor' },
};

const STATUS_META = {
  not_started:     { label: 'Not Started',     icon: Clock,         color: '#6b7280', bg: '#f3f4f6' },
  in_progress:     { label: 'In Progress',     icon: Clock,         color: '#b45309', bg: '#fef3c7' },
  under_review:    { label: 'Under Review',    icon: Search,        color: '#1d4ed8', bg: '#dbeafe' },
  complete:        { label: 'Complete',        icon: CheckCircle2,  color: '#065f46', bg: '#d1fae5' },
  action_required: { label: 'Action Required', icon: AlertTriangle, color: '#9f1239', bg: '#ffe4e6' },
  rejected:        { label: 'Rejected',        icon: AlertTriangle, color: '#7f1d1d', bg: '#fee2e2' },
};

const PHOTO_CATS = [
  { key: 'before',      label: 'Before' },
  { key: 'preparation', label: 'Preparation / Base' },
  { key: 'during',      label: 'During Installation' },
  { key: 'finished',    label: 'Finished Project' },
];

export default function InstallerApproval() {
  const { brand: brandParam } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [openStage, setOpenStage] = useState(2);

  const brand = BRAND_META[brandParam] ? brandParam : 'probound';
  const theme = BRAND_META[brand];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [docRes, lblRes] = await Promise.all([
        fetch(`${API_URL}/api/installer/approvals/${brand}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/installer/approvals/labels`, { credentials: 'include' }),
      ]);
      if (!docRes.ok) throw new Error('Failed to load');
      setDoc(await docRes.json());
      if (lblRes.ok) setLabels(await lblRes.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brand]);

  useEffect(() => { load(); }, [load]);

  const progress = useMemo(() => {
    if (!doc) return { done: 0, total: 6 };
    // The 6 required stages. Stage 7 (Customer Review) has been retired.
    const keys = ['stage1_training','stage2_profile','stage3_online','stage4_insurance','stage5_first_install','stage6_documentation','stage8_technical'];
    // stage8_technical is only "complete" once Ox approve you, so it counts too.
    // Keep total at 6 to represent the 6 stages the installer completes; stage 8
    // marking as complete = approval granted (headline changes to Ox Approved).
    return { done: keys.filter(k => k !== 'stage8_technical' && (doc[k]||{}).status === 'complete').length, total: 6 };
  }, [doc]);
  const pct = Math.round((progress.done / progress.total) * 100);
  const awaitingOx = useMemo(() => {
    if (!doc) return [];
    return [['stage3_online', 3], ['stage4_insurance', 4], ['stage6_documentation', 6], ['stage8_technical', 8]]
      .filter(([k]) => (doc[k] || {}).status === 'under_review').map(([, n]) => n);
  }, [doc]);

  const allButFinalComplete = useMemo(() => {
    if (!doc) return false;
    // Stage 7 is optional and does NOT block the technical-review submit.
    // Ox reviews stages 3/4/6 together with the final decision, so "submitted" counts.
    const done = (k) => (doc[k]||{}).status === 'complete';
    const sent = (k) => ['complete', 'under_review'].includes((doc[k]||{}).status);
    return ['stage1_training','stage2_profile','stage5_first_install'].every(done)
      && ['stage3_online','stage4_insurance','stage6_documentation'].every(sent);
  }, [doc]);

  const submitForFinal = async () => {
    try {
      const res = await fetch(`${API_URL}/api/installer/approvals/${brand}/submit-for-final`, { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed');
      setDoc(await res.json());
      toast.success('Submitted for final technical review.');
    } catch (e) { toast.error(e.message); }
  };

  if (loading || !doc) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;

  const portfolio = <PortfolioSection brand={brand} theme={theme} labels={labels[brand] || {}} />;

  const stages = [
    { n: 1, key: 'stage1_training',       title: 'Create Account In App',            subtitle: 'Your Ox Resin portal account is live.' },
    { n: 2, key: 'stage2_profile',        title: 'Complete Company Profile',         subtitle: 'Business details, plus your postcode and how far you travel — so we can send you nearby leads.' },
    { n: 3, key: 'stage3_online',         title: 'Online Presence',                  subtitle: 'Add your business website (social links optional).' },
    { n: 4, key: 'stage4_insurance',      title: 'Public Liability Insurance',       subtitle: 'Upload a valid certificate.' },
    { n: 5, key: 'stage5_first_install',  title: 'Submit Portfolio',                 subtitle: 'Tell us about a recent project — applies to new trainees and existing installers.' },
    { n: 6, key: 'stage6_documentation',  title: 'Document the Project',             subtitle: 'Upload required photos of that install.' },
    { n: 7, key: 'stage8_technical',      title: 'Technical Approval',               subtitle: 'Ox Resin reviews your submission and grants approval.' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="text-white" style={{ background: theme.headerBg, borderBottom: `3px solid ${theme.accent}` }}>
        <div className="max-w-[1100px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate(theme.back)} data-testid="approval-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
              <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: theme.accent }} /> Become Ox Approved — {theme.label}
            </h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">Complete the 6 required stages to unlock your Ox Approved Installer badge.</p>
          </div>
          <Button onClick={() => navigate('/')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-3 sm:px-4 py-5 space-y-4">
        <StrategyCallCard brand={brand} theme={theme} />
        <LeadCoverageCard theme={theme} />

        {/* Progress + overall status */}
        <Card className="p-4 sm:p-5" data-testid="approval-progress">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">{progress.done}/6 Complete</div>
            <StatusBadge status={doc.overall_status === 'approved' ? 'complete' : doc.overall_status === 'rejected' || doc.overall_status === 'lapsed' ? 'rejected' : doc.overall_status === 'submitted' ? 'under_review' : 'in_progress'} label={doc.overall_status === 'approved' ? `${theme.label} Ox Approved` : doc.overall_status === 'lapsed' ? 'Ox Approved paused — place an order to reactivate' : doc.overall_status === 'submitted' ? 'Submitted for review' : doc.overall_status === 'rejected' ? 'Application rejected' : 'In progress'} />
          </div>
          <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
            <div data-testid="approval-progress-bar" className="h-full transition-all" style={{ width: `${pct}%`, background: theme.accent }} />
          </div>
          {awaitingOx.length > 0 && doc.overall_status !== 'approved' && (
            <p className="mt-3 text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2" data-testid="approval-awaiting-ox">
              <Search className="w-4 h-4 inline mr-1 -mt-0.5" />
              {doc.overall_status === 'submitted'
                ? 'Everything is with the Ox team now — we review all your submitted stages together and will email you the outcome.'
                : `Stage ${awaitingOx.join(' & ')} submitted — nothing more needed there. Finish the remaining stages and your application goes to the Ox team automatically.`}
            </p>
          )}
          {doc.overall_status !== 'approved' && allButFinalComplete && doc.stage8_technical.status !== 'under_review' && (
            <Button data-testid="approval-submit-final" onClick={submitForFinal} className="mt-4" style={{ background: theme.buttonBg, color: theme.buttonText }}>
              <Send className="w-4 h-4 mr-1.5" /> Submit for technical review
            </Button>
          )}
        </Card>

        {doc.overall_status === 'approved' && portfolio}

        {stages.map(s => (
          <StageCard
            key={s.n}
            stage={s}
            doc={doc}
            theme={theme}
            brand={brand}
            labels={labels[brand] || {}}
            open={openStage === s.n}
            onToggle={() => setOpenStage(openStage === s.n ? null : s.n)}
            onChange={(updated) => setDoc(updated)}
          />
        ))}

        {doc.overall_status !== 'approved' && portfolio}
      </div>
    </div>
  );
}

function StatusBadge({ status, label }) {
  const meta = STATUS_META[status] || STATUS_META.not_started;
  const Icon = meta.icon;
  return (
    <span data-testid={`status-${status}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full" style={{ background: meta.bg, color: meta.color }}>
      <Icon className="w-3.5 h-3.5" /> {label || meta.label}
    </span>
  );
}

function StageCard({ stage, doc, theme, brand, labels, open, onToggle, onChange }) {
  const data = doc[stage.key] || {};
  return (
    <Card className="overflow-hidden" data-testid={`stage-card-${stage.n}`}>
      <button onClick={onToggle} data-testid={`stage-toggle-${stage.n}`} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left">
        <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shrink-0" style={{ background: data.status === 'complete' ? theme.buttonBg : '#e5e7eb', color: data.status === 'complete' ? theme.buttonText : '#374151' }}>
          {data.status === 'complete' ? <CheckCircle2 className="w-5 h-5" /> : stage.n}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm sm:text-base text-gray-900 flex items-center gap-2 flex-wrap">
            {stage.title}
            {stage.optional && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">Optional</span>
            )}
          </div>
          <div className="text-xs text-gray-500">{stage.subtitle}</div>
        </div>
        <StatusBadge status={data.status || 'not_started'} />
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div className="border-t border-gray-100 p-4 bg-gray-50/50">
          {stage.n === 1 && <Stage1View />}
          {stage.n === 2 && <Stage2View data={data} theme={theme} brand={brand} onChange={onChange} />}
          {stage.n === 3 && <Stage3Form data={data} brand={brand} theme={theme} onChange={onChange} />}
          {stage.n === 4 && <Stage4Form data={data} brand={brand} theme={theme} onChange={onChange} />}
          {stage.n === 5 && <Stage5Form data={data} brand={brand} theme={theme} labels={labels} onChange={onChange} />}
          {stage.n === 6 && <Stage6Form data={data} brand={brand} theme={theme} onChange={onChange} />}
          {stage.n === 7 && <Stage7Form data={data} brand={brand} theme={theme} onChange={onChange} />}
          {stage.n === 8 && <Stage8View data={data} doc={doc} theme={theme} />}
        </div>
      )}
    </Card>
  );
}

// ──────────────────────────── Stage views ────────────────────────────

function Stage1View() {
  return (
    <div className="p-3 rounded bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm flex items-start gap-2">
      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
      <div>
        <div className="font-bold">Account created.</div>
        <div className="text-xs text-emerald-800/80">You’re signed in to the Ox Resin Installer Portal — this stage is automatically complete.</div>
      </div>
    </div>
  );
}

function Stage2View({ data, theme, brand, onChange }) {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const status = data.status;
  const [pc, setPc] = useState(data.postcode || '');
  const [miles, setMiles] = useState(data.coverage_radius_miles || 30);
  const [saving, setSaving] = useState(false);
  const needsLocation = !data.postcode || !data.coverage_radius_miles;
  const summary = [
    ['Company name', data.company_name],
    ['Mobile', data.mobile],
    ['Email', data.email],
    ['Business address', data.business_address],
    ['Postcode', data.postcode],
    ['Travel radius', data.coverage_radius_miles ? `${data.coverage_radius_miles} miles` : ''],
  ];
  const saveLocation = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/me/postcode-nudge/postcode`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcode: pc, coverage_radius_miles: parseInt(miles, 10) || 30 }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(formatApiError(body) || 'Could not save');
      const fresh = await fetch(`${API_URL}/api/installer/approvals/${brand}`, { credentials: 'include' });
      if (fresh.ok) onChange(await fresh.json());
      checkAuth?.();
      toast.success(`Saved${body.town ? ` — ${body.town}` : ''}. You'll now be matched to leads within ${body.coverage_radius_miles || miles} miles.`);
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-3 text-sm">
      <p className="text-xs text-gray-600">
        Complete your business details on the Business Profile page, and tell us where you&apos;re based and how far you travel — once every field is filled in, this stage is automatically marked complete.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {summary.map(([k, v]) => (
          <div key={k} className="text-xs">
            <span className="text-gray-500">{k}: </span>
            <span className={v ? 'text-gray-900 font-semibold' : 'text-rose-600 italic'}>{v || 'missing'}</span>
          </div>
        ))}
      </div>
      <form onSubmit={saveLocation} className={`rounded-lg border p-3 ${needsLocation ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'}`} data-testid="s2-location-form">
        <div className="flex items-center gap-1.5 text-xs font-bold text-gray-800 mb-2"><MapPin className="w-3.5 h-3.5" /> Where you&apos;re based &amp; how far you travel</div>
        <p className="text-[11px] text-gray-600 mb-2">We match customer enquiries to the nearest approved installers. Your postcode stays private — it&apos;s only used to work out distance to a job.</p>
        <div className="flex flex-wrap items-center gap-2">
          <Input value={pc} onChange={(e) => setPc(e.target.value)} placeholder="Your postcode" className="h-9 w-36 bg-white uppercase" required data-testid="s2-postcode" />
          <div className="flex items-center gap-1"><Input type="number" min="1" max="500" value={miles} onChange={(e) => setMiles(e.target.value)} className="h-9 w-20 bg-white" data-testid="s2-radius" /><span className="text-xs text-gray-700">miles</span></div>
          <Button type="submit" size="sm" disabled={saving || !pc.trim()} style={{ background: theme.buttonBg, color: theme.buttonText }} data-testid="s2-location-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : needsLocation ? 'Save location' : 'Update'}
          </Button>
        </div>
      </form>
      <Button
        onClick={() => navigate('/settings')}
        data-testid="s2-open-profile"
        variant="outline"
        className="font-semibold"
      >
        {status === 'complete' ? 'View / Update Business Profile' : 'Open Business Profile'}
      </Button>
    </div>
  );
}

function Stage3Form({ data, brand, theme, onChange }) {
  const [form, setForm] = useState({
    website: data.website || '', facebook: data.facebook || '',
    instagram: data.instagram || '', tiktok: data.tiktok || '', linkedin: data.linkedin || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    if (!form.website.trim()) return toast.error('Please add your website URL.');
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/installer/approvals/${brand}/stage3/submit`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Save failed');
      onChange(await res.json()); toast.success('Submitted for review.');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3 text-sm">
      {data.admin_decision === 'requires_improvement' && data.admin_notes && (
        <div className="p-3 rounded bg-rose-50 border border-rose-200 text-rose-900 text-xs">
          <strong>Action required:</strong> {data.admin_notes}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Website *" value={form.website} onChange={set('website')} testid="s3-website" placeholder="https://" className="sm:col-span-2" />
        <Field label="Facebook (optional)" value={form.facebook} onChange={set('facebook')} testid="s3-facebook" placeholder="https://facebook.com/…" />
        <Field label="Instagram (optional)" value={form.instagram} onChange={set('instagram')} testid="s3-instagram" />
        <Field label="TikTok (optional)" value={form.tiktok} onChange={set('tiktok')} testid="s3-tiktok" />
        <Field label="LinkedIn (optional)" value={form.linkedin} onChange={set('linkedin')} testid="s3-linkedin" />
      </div>
      <Button onClick={submit} disabled={saving} data-testid="s3-submit" style={{ background: theme.buttonBg, color: theme.buttonText }} className="font-semibold">{saving ? 'Submitting\u2026' : 'Submit for review'}</Button>
    </div>
  );
}

function Stage4Form({ data, brand, theme, onChange }) {
  const [form, setForm] = useState({
    insurance_company: data.insurance_company || '',
    policy_number: data.policy_number || '',
    expiry_date: data.expiry_date || '',
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    if (!form.insurance_company || !form.policy_number || !form.expiry_date) return toast.error('Please complete all insurance details.');
    if (!file && !data.certificate_filename) return toast.error('Please upload your certificate.');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('insurance_company', form.insurance_company);
      fd.append('policy_number', form.policy_number);
      fd.append('expiry_date', form.expiry_date);
      if (file) fd.append('file', file);
      const res = await fetch(`${API_URL}/api/installer/approvals/${brand}/stage4/submit`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Upload failed');
      onChange(await res.json()); toast.success('Submitted for review.'); setFile(null);
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); }
  };
  return (
    <div className="space-y-3 text-sm">
      {data.status === 'action_required' && data.admin_notes && (
        <div className="p-3 rounded bg-rose-50 border border-rose-200 text-rose-900 text-xs"><strong>Action required:</strong> {data.admin_notes}</div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Insurance Company *" value={form.insurance_company} onChange={set('insurance_company')} testid="s4-company" />
        <Field label="Policy Number *" value={form.policy_number} onChange={set('policy_number')} testid="s4-policy" />
        <Field label="Expiry Date *" value={form.expiry_date} onChange={set('expiry_date')} testid="s4-expiry" type="date" />
        <div>
          <div className="text-xs font-bold text-gray-700 mb-1">Certificate (PDF, PNG, JPG) *</div>
          <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="s4-file" className="text-xs" />
          {data.certificate_filename && !file && (
            <div className="mt-1 text-[11px] text-gray-500 inline-flex items-center gap-1"><FileText className="w-3 h-3" />{data.certificate_filename} on file</div>
          )}
        </div>
      </div>
      <Button onClick={submit} disabled={uploading} data-testid="s4-submit" style={{ background: theme.buttonBg, color: theme.buttonText }} className="font-semibold">{uploading ? 'Uploading\u2026' : 'Submit for review'}</Button>
    </div>
  );
}

function Stage5Form({ data, brand, theme, labels, onChange }) {
  const [form, setForm] = useState({
    project_name: data.project_name || '', project_address: data.project_address || '',
    installation_date: data.installation_date || '', square_metres: data.square_metres || '',
    material_used: data.material_used || '', mix_or_blend: data.mix_or_blend || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    if (!form.project_name || !form.project_address || !form.installation_date || !form.square_metres || !form.material_used || !form.mix_or_blend) {
      return toast.error('Please complete every field.');
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/installer/approvals/${brand}/stage5`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, square_metres: parseFloat(form.square_metres) }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Save failed');
      onChange(await res.json()); toast.success('Saved.');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3 text-sm">
      <p className="text-xs text-gray-500">Completion of this stage unlocks Stage 6 (project documentation).</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Project Name *" value={form.project_name} onChange={set('project_name')} testid="s5-name" />
        <Field label="Installation Date *" value={form.installation_date} onChange={set('installation_date')} testid="s5-date" type="date" />
        <Field label="Project Address *" value={form.project_address} onChange={set('project_address')} testid="s5-address" className="sm:col-span-2" />
        <Field label="Square Metres *" value={form.square_metres} onChange={set('square_metres')} testid="s5-area" type="number" />
        <Field label={`${labels.material_used_label || 'Material Used'} *`} value={form.material_used} onChange={set('material_used')} testid="s5-material" />
        <Field label={`${labels.mix_or_blend_label || 'Mix / Blend'} *`} value={form.mix_or_blend} onChange={set('mix_or_blend')} testid="s5-blend" className="sm:col-span-2" />
      </div>
      <Button onClick={save} disabled={saving} data-testid="s5-save" style={{ background: theme.buttonBg, color: theme.buttonText }} className="font-semibold">{saving ? 'Saving\u2026' : 'Save portfolio entry'}</Button>
    </div>
  );
}

function Stage6Form({ data, brand, theme, onChange }) {
  const photos = data.photos || [];
  const have = new Set(photos.map(p => p.category));
  const [submitting, setSubmitting] = useState(false);
  const uploadOne = async (cat, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('category', cat);
    fd.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/installer/approvals/${brand}/stage6/photo`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Upload failed');
      onChange(await res.json()); toast.success(`Uploaded ${cat}.`);
    } catch (e) { toast.error(e.message); }
  };
  const submitForReview = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/installer/approvals/${brand}/stage6/submit`, { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed');
      onChange(await res.json()); toast.success('Submitted for review.');
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="space-y-3 text-sm">
      {data.status === 'action_required' && data.admin_notes && (
        <div className="p-3 rounded bg-rose-50 border border-rose-200 text-rose-900 text-xs"><strong>Action required:</strong> {data.admin_notes}</div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {PHOTO_CATS.map(p => {
          const got = have.has(p.key);
          const photo = photos.find(x => x.category === p.key);
          const src = photo ? `${API_URL}/api/installer/approvals/${brand}/stage6/photo/${photo.gridfs_id}` : null;
          return (
            <div key={p.key} className={`p-3 rounded border-2 ${got ? 'border-emerald-300 bg-emerald-50/40' : 'border-dashed border-gray-300'}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-bold text-gray-800">{p.label} {got ? '✓' : '*'}</span>
                {got && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
              </div>
              {src && (
                <a href={src} target="_blank" rel="noreferrer" title="Open full size in a new tab" data-testid={`s6-photo-preview-${p.key}`} className="block mb-2 w-full rounded-md overflow-hidden border border-emerald-200 hover:border-emerald-500">
                  <img src={src} alt={p.label} loading="lazy" className="w-full h-28 object-cover bg-gray-100" />
                  <div className="px-2 py-1 text-[10px] text-emerald-900 truncate bg-white">{photo.filename}</div>
                </a>
              )}
              <input type="file" accept="image/*" onChange={(e) => uploadOne(p.key, e.target.files?.[0])} data-testid={`s6-photo-${p.key}`} className="text-xs" />
            </div>
          );
        })}
      </div>
      <div className="p-3 rounded border-2 border-dashed border-gray-300">
        <div className="text-xs font-bold text-gray-800 mb-1.5">Short video (optional)</div>
        <input type="file" accept="video/*" onChange={(e) => uploadOne('video', e.target.files?.[0])} data-testid="s6-video" className="text-xs" />
        {data.video_filename && (
          <a href={`${API_URL}/api/installer/approvals/${brand}/stage6/photo/${data.video_gridfs_id}`} target="_blank" rel="noreferrer" data-testid="s6-video-preview" className="mt-1 text-[11px] text-blue-700 underline inline-block">{data.video_filename} on file — open</a>
        )}
      </div>
      <Button onClick={submitForReview} disabled={submitting || have.size < PHOTO_CATS.length} data-testid="s6-submit" style={{ background: theme.buttonBg, color: theme.buttonText }} className="font-semibold">
        <Upload className="w-4 h-4 mr-1.5" />{submitting ? 'Submitting\u2026' : `Submit ${have.size}/${PHOTO_CATS.length} photos for review`}
      </Button>
    </div>
  );
}

function Stage7Form({ data, brand, theme, onChange }) {
  const [form, setForm] = useState({ customer_name: data.customer_name || '', customer_email: data.customer_email || '' });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const send = async () => {
    if (!form.customer_name || !form.customer_email) return toast.error('Please add customer name and email.');
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/installer/approvals/${brand}/stage7/send`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed');
      const result = await res.json();
      onChange(result);
      toast.success('Review request sent.');
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };
  const reviewUrl = data.review_link_token ? `${window.location.origin}/review/${data.review_link_token}` : '';
  const copy = async () => {
    try { await navigator.clipboard.writeText(reviewUrl); toast.success('Link copied.'); } catch { toast.error('Couldn\u2019t copy.'); }
  };
  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Customer Name *" value={form.customer_name} onChange={set('customer_name')} testid="s7-name" />
        <Field label="Customer Email *" value={form.customer_email} onChange={set('customer_email')} testid="s7-email" type="email" />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={send} disabled={sending} data-testid="s7-send" style={{ background: theme.buttonBg, color: theme.buttonText }} className="font-semibold"><Send className="w-4 h-4 mr-1.5" />{data.review_sent_at ? 'Resend review request' : 'Send review request'}</Button>
        {data.review_link_token && <Button variant="outline" onClick={copy} data-testid="s7-copy" style={{ borderColor: theme.buttonBg, color: theme.buttonBg }}><Copy className="w-4 h-4 mr-1.5" />Copy link</Button>}
      </div>
      {data.review_received_at && (
        <div className="p-3 rounded bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs">
          <div className="flex items-center gap-1 mb-1">{[1,2,3,4,5].map(n => <Star key={n} className={`w-4 h-4 ${n <= data.rating ? 'fill-amber-400 text-amber-500' : 'text-gray-300'}`} />)} <strong className="ml-1.5">{data.rating}/5</strong></div>
          <div className="italic">&ldquo;{data.review_text}&rdquo;</div>
          <div className="text-[10px] uppercase tracking-wider text-gray-600 mt-1.5">Received {new Date(data.review_received_at).toLocaleDateString('en-GB')}</div>
        </div>
      )}
    </div>
  );
}

function Stage8View({ data, doc, theme }) {
  const status = data.status;
  if (status === 'complete') {
    return <div className="p-4 rounded bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm" data-testid="s8-approved">🎉 You’re an Ox Approved {theme.label} Installer. {data.decision_notes && <em>— {data.decision_notes}</em>}</div>;
  }
  if (status === 'rejected') {
    return <div className="p-4 rounded bg-rose-50 border border-rose-200 text-rose-900 text-sm" data-testid="s8-rejected">Application rejected. {data.decision_notes && <em>— {data.decision_notes}</em>} Contact Ox to discuss next steps.</div>;
  }
  if (status === 'under_review') {
    return <div className="p-4 rounded bg-blue-50 border border-blue-200 text-blue-900 text-sm" data-testid="s8-under-review">Under technical review — we’ll be in touch shortly.</div>;
  }
  if (status === 'action_required') {
    return <div className="p-4 rounded bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="s8-action">More info needed. {data.decision_notes && <em>— {data.decision_notes}</em>}</div>;
  }
  return <div className="text-sm text-gray-600">Complete Stages 1–6 and your application is sent to the Ox team automatically. (Stage 7 is optional.)</div>;
}

function Field({ label, value, onChange, testid, type = 'text', className = '', placeholder, labelStyle }) {
  return (
    <div className={className}>
      <label className="text-xs font-bold" style={labelStyle || { color: '#374151' }}>{label}</label>
      <Input type={type} value={value} onChange={onChange} data-testid={testid} placeholder={placeholder} className="mt-1 bg-white text-gray-900" />
    </div>
  );
}

// ─────────────────────── Strategy Call (book a call) ────────────────────────

const TIME_SLOTS = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'];

function StrategyCallCard({ brand, theme }) {
  const [open, setOpen] = useState(false);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadCalls = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/installer/strategy-calls?brand=${brand}`, { credentials: 'include' });
      if (res.ok) setCalls(await res.json());
    } catch (e) { /* silent */ }
  }, [brand]);

  useEffect(() => { loadCalls(); }, [loadCalls]);

  const cancelCall = async (id) => {
    if (!window.confirm('Cancel this strategy call request?')) return;
    try {
      const res = await fetch(`${API_URL}/api/installer/strategy-calls/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed');
      toast.success('Call cancelled.');
      loadCalls();
    } catch (e) { toast.error(e.message); }
  };

  const upcoming = calls.find(c => c.status === 'pending' || c.status === 'confirmed');

  return (
    <Card className="p-4 sm:p-5" data-testid="strategy-call-card">
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: `${theme.buttonBg}1A`, color: theme.buttonBg }}>
            <Phone className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-base text-gray-900">Book My Marketing Call</h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
              Book a call with one of the Ox Resin team — we’ll talk through your website, socials, portfolio and paperwork so you can get OxApproved faster and start winning more work.
            </p>
          </div>
        </div>
        <Button
          onClick={() => setOpen(true)}
          data-testid="strategy-call-open"
          className="font-semibold shrink-0"
          style={{ background: theme.buttonBg, color: theme.buttonText }}
        >
          <CalendarIcon className="w-4 h-4 mr-1.5" /> Book My Marketing Call
        </Button>
      </div>

      {upcoming && (
        <div className="mt-4 p-3 rounded border bg-white text-xs" data-testid="strategy-call-upcoming">
          <div className="flex items-center justify-between gap-2">
            <div>
              <span className="font-bold text-gray-900">
                {upcoming.status === 'confirmed' ? 'Confirmed for ' : 'Requested for '}
                {new Date(`${upcoming.requested_date}T${upcoming.requested_time}:00`).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
              <span className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full" style={{
                background: upcoming.status === 'confirmed' ? '#d1fae5' : '#fef3c7',
                color: upcoming.status === 'confirmed' ? '#065f46' : '#b45309',
              }}>{upcoming.status}</span>
            </div>
            <button onClick={() => cancelCall(upcoming.id)} data-testid="strategy-call-cancel" className="text-rose-600 hover:underline inline-flex items-center gap-1"><X className="w-3 h-3" /> Cancel</button>
          </div>
          {upcoming.staff_notes && <div className="mt-1 text-gray-600 italic">Note from Ox: {upcoming.staff_notes}</div>}
        </div>
      )}

      <StrategyCallDialog
        open={open}
        onClose={() => setOpen(false)}
        brand={brand}
        theme={theme}
        onCreated={() => { setOpen(false); loadCalls(); }}
      />
    </Card>
  );
}

function StrategyCallDialog({ open, onClose, brand, theme, onCreated }) {
  const [prefill, setPrefill] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '',
    requested_date: '', requested_time: '09:00', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Pull business profile on open so the form is pre-populated.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/settings`, { credentials: 'include' });
        if (res.ok) {
          const s = await res.json();
          setPrefill(s);
          setForm(f => ({
            ...f,
            name: s.company_name || f.name,
            email: s.company_email || f.email,
            phone: s.company_phone || f.phone,
            company: s.company_name || f.company,
          }));
        }
      } catch (e) { /* silent */ }
    })();
  }, [open]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Mon-Fri only — flip the underlying <input type=date> to ignore weekends.
  const onDateChange = (e) => {
    const v = e.target.value;
    if (!v) return setForm(f => ({ ...f, requested_date: '' }));
    const d = new Date(`${v}T00:00:00`);
    if (d.getDay() === 0 || d.getDay() === 6) {
      return toast.error('Please pick a weekday (Mon\u2013Fri).');
    }
    setForm(f => ({ ...f, requested_date: v }));
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.requested_date || !form.requested_time) {
      return toast.error('Please fill in your name, email and pick a date + time.');
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/installer/strategy-calls`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, brand }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed');
      toast.success('Request sent — we\u2019ll confirm by email shortly.');
      onCreated && onCreated();
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  // Min date = today (formatted YYYY-MM-DD)
  const today = new Date().toISOString().slice(0, 10);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-lg border-0"
        data-testid="strategy-call-dialog"
        style={{ backgroundColor: theme.buttonBg, color: theme.buttonText }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: theme.buttonText }}>
            <Phone className="w-4 h-4" /> Book My Marketing Call
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-xs" style={{ color: theme.buttonText, opacity: 0.85 }}>
            Pick a weekday slot between 9am and 4pm. {prefill?.company_name && <span>We’ve pre-filled the form from your business profile — edit anything if it’s wrong.</span>}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Your Name *" value={form.name} onChange={set('name')} testid="strategy-name" labelStyle={{ color: theme.buttonText }} />
            <Field label="Company" value={form.company} onChange={set('company')} testid="strategy-company" labelStyle={{ color: theme.buttonText }} />
            <Field label="Email *" type="email" value={form.email} onChange={set('email')} testid="strategy-email" labelStyle={{ color: theme.buttonText }} />
            <Field label="Phone" value={form.phone} onChange={set('phone')} testid="strategy-phone" labelStyle={{ color: theme.buttonText }} />
            <div>
              <label className="text-xs font-bold" style={{ color: theme.buttonText }}>Date * (Mon–Fri)</label>
              <Input type="date" value={form.requested_date} onChange={onDateChange} min={today} data-testid="strategy-date" className="mt-1 bg-white text-gray-900" />
            </div>
            <div>
              <label className="text-xs font-bold" style={{ color: theme.buttonText }}>Time *</label>
              <select
                value={form.requested_time}
                onChange={set('requested_time')}
                data-testid="strategy-time"
                className="mt-1 w-full h-9 px-3 rounded-md border border-input bg-white text-sm text-gray-900"
              >
                {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold" style={{ color: theme.buttonText }}>Anything you’d like us to look at?</label>
            <Textarea value={form.notes} onChange={set('notes')} data-testid="strategy-notes" rows={3} placeholder="Optional — e.g. ‘Stuck on insurance upload’ or ‘Want feedback on my first project’" className="mt-1 bg-white text-gray-900 placeholder:text-gray-400" />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            data-testid="strategy-cancel"
            className="hover:bg-white/10"
            style={{ color: theme.buttonText, border: `1px solid ${theme.buttonText}55` }}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="strategy-submit"
            className="font-semibold hover:opacity-90"
            style={{ background: theme.buttonText, color: theme.buttonBg }}
          >
            {submitting ? 'Sending\u2026' : 'Send request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
